import React, {
	createContext,
	memo,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
} from 'react';
import ReactDOM from 'react-dom';

import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { type LoaderKey, type FullscreenLoaderProps } from './FullLoaderScreenLoader.types';
import { FullLoaderAnimationStyles, Overlay } from './FullLoaderScreenLoader.styles';

export type { LoaderKey };

interface LoaderContextValue {
	setLoading: (key: LoaderKey, isLoading: boolean) => void;
	isAnyLoading: boolean;
}

const LoaderContext = createContext<LoaderContextValue>({
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	setLoading: () => {},
	isAnyLoading: false,
});

const DEBOUNCE_TIME = 300;

export const FullLoaderScreenLoaderProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
	const [loadingStates, setLoadingStates] = useState<Record<LoaderKey, boolean>>({
		suspense: false,
		translations: false,
		userState: false,
		protectedRoute: false,
		guestRoute: false,
		pageLoading: false,
		signingOut: false,
	});
	const containerRef = useRef<HTMLDivElement | null>(null);
	const debounceTimer = useRef<NodeJS.Timeout | null>(null);

	const isAnyLoading = Object.values(loadingStates).some(Boolean);

	const setLoading = useCallback((key: LoaderKey, isLoading: boolean) => {
		setLoadingStates((prev) => {
			// Skip update if value hasn't changed (idempotent)
			if (prev[key] === isLoading) {
				return prev;
			}
			return { ...prev, [key]: isLoading };
		});
	}, []);

	/** Side-effect: show/hide overlay based on isAnyLoading */
	useEffect(() => {
		const container = containerRef.current;
		if (!container) {
			return;
		}

		if (isAnyLoading) {
			// Clear any pending hide timer
			if (debounceTimer.current) {
				clearTimeout(debounceTimer.current);
				debounceTimer.current = null;
			}
			// Show overlay
			container.style.visibility = 'visible';
		} else {
			// Debounce hide
			debounceTimer.current = setTimeout(() => {
				container.style.visibility = 'hidden';
				debounceTimer.current = null;
			}, DEBOUNCE_TIME);
		}

		return () => {
			if (debounceTimer.current) {
				clearTimeout(debounceTimer.current);
			}
		};
	}, [isAnyLoading]);

	const contextValue = React.useMemo(
		() => ({ setLoading, isAnyLoading }),
		[setLoading, isAnyLoading]
	);

	return (
		<LoaderContext.Provider value={contextValue}>
			{children}
			{typeof document !== 'undefined' &&
				ReactDOM.createPortal(
					<Overlay ref={containerRef} data-testid={TEST_ELEMENT_IDS.FULLSCREEN_LOADER}>
						<FullLoaderAnimationStyles>
							<svg viewBox="0 0 160 160" xmlns="http://www.w3.org/2000/svg">
								<rect
									className="box box-1"
									x="9.945"
									y="64"
									width="32"
									height="32"
									rx="8"
									fill="#959E9E"
								/>
								<rect
									className="box box-2"
									x="46.055"
									y="64"
									width="32"
									height="32"
									rx="8"
									fill="#959E9E"
								/>
								<rect
									className="box box-3"
									x="82.055"
									y="64"
									width="32"
									height="32"
									rx="8"
									fill="#959E9E"
								/>
								<rect
									className="box box-4"
									x="118.055"
									y="64"
									width="32"
									height="32"
									rx="8"
									fill="#959E9E"
								/>
							</svg>
						</FullLoaderAnimationStyles>
					</Overlay>,
					document.body
				)}
		</LoaderContext.Provider>
	);
};

/**
 * Hook to control the fullscreen loader from any component.
 * Shows the loader when mounted, hides when unmounted.
 *
 * @param key - The named loading state key
 */
export const useFullscreenLoader = (key: LoaderKey) => {
	const { setLoading } = useContext(LoaderContext);

	useEffect(() => {
		setLoading(key, true);
		return () => setLoading(key, false);
	}, [key, setLoading]);
};

/**
 * Drop <FullscreenLoader name="..." /> anywhere to set the named loading state
 * to true on mount and false on unmount. If an animation is already running,
 * it will continue until all loading states are false.
 *
 * This is idempotent - mounting the same key multiple times (e.g., during
 * React StrictMode double-mounting) won't cause issues.
 */
export const FullscreenLoader = memo(({ name }: FullscreenLoaderProps) => {
	useFullscreenLoader(name);
	return null;
});

FullscreenLoader.displayName = 'FullscreenLoader';
